import * as THREE from 'three';
import { TweenLite, Sine } from 'gsap';
import sprite from './img/sprite-3.png';
import context from './context';
import { vertexShader, fragmentShader } from './shader.js';
import { gamEvent } from '../modules/animation';
import { sineInOut } from '../util/transitions'
export default class Particles {

    //transition durations
    static clockLimitSamePage = 2
    static clockLimitNextPage = 4

    constructor(props) {
        
        this.props = props;
        this.clock = new THREE.Clock(false);
        this.mesh = this.createMesh();
        this.sizeTween = null;

        this.clockLimit = 0;
        this.transitioningToNewPage = false;

        this.colors = []
        this.positions = []
        this.targetColors = null
        this.targetPositions = null

        //the variables above are not what's really tweening. These are stored here.
        this.tweenStart = {}
        this.tweenEnd = {}

        this.componentDidMount();
    }

    createMesh = () => {

        const spritePath = `${window.pw.templates}assets/${sprite}`;

        const geometry = new THREE.BufferGeometry();
        const material = new THREE.ShaderMaterial({
            uniforms: {
                elapsedTime: { value: 0.0 },
                duration: { value: 2.0 },
                size: { value: 0.2 },
                pointTexture: { 
                    value: new THREE.TextureLoader().load(spritePath)
                },
                //the previous PointMaterial used sizeAttenuation
                //this is a quick+dirty way to reproduce the point sizes before the shader refactoring
                //@see https://github.com/mrdoob/three.js/issues/10385
                scale: { value: window.innerHeight * 0.5 },
            },
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            vertexColors: true,
            transparent: true,
            depthTest: false,
            blending: THREE.AdditiveBlending,
        });


        return new THREE.Points(geometry, material);
    }

	componentDidMount = () => {
        this.props.wrap.add(this.mesh);
        this.props.addLoop(this.animate);
        window.document.addEventListener('gamMapLoaded', this.handleMapLoaded);
        window.document.addEventListener('gamLinkLoading', this.handleLink);
    }
    
    componentWillUnmount = () => { 
        this.props.wrap.remove(this.mesh);        
        this.props.removeLoop(this.animate);
        window.document.removeEventListener('gamMapLoaded', this.handleMapLoaded);
        window.document.removeEventListener('gamLinkLoading', this.handleLink);
    }

    handleMapLoaded = (e) => {

        //options
        const defaults = {
            psize: 0.2
        }
        const options = Object.assign(defaults, e.detail.options);
        let { psize } = options;

        this.currentHelper = e.detail.helper;
        const { positions, colors } = this.currentHelper.readCanvas();

        //@debug
        if(process.env.NODE_ENV === 'development') {
            console.log('handleMapLoaded', e.detail, options, positions.length/3, 'Vertices');
        }

        //initial? Instant
        if(context.isInitial) {
            this.colors = colors;
            this.positions = positions;
            this.targetColors = colors;
            this.targetPositions = positions;

            this.tweenStart = this.tweenEnd = { pos: positions, clr: colors }

            const p32 = new THREE.Float32BufferAttribute(this.positions, 3)
            const c32 = new THREE.Float32BufferAttribute(this.colors, 3)
            this.mesh.geometry.setAttribute('position', p32);
            this.mesh.geometry.setAttribute('targetPosition', p32);
            this.mesh.geometry.setAttribute('color', c32);
            this.mesh.geometry.setAttribute('targetColor', c32);
            this.mesh.material.uniforms.size.value = psize;
            this.currentHelper && this.currentHelper.play();

            //unset initial so all future maps will be tweened
            context.toggleInitial();
            return;
        }

        //if a new map is loaded while there is still a transition running, 
        //we have to calculate the actual current positions and colors (what happens in the shader normally)
        //so we can smoothly transition from this in-between state to the next target
        const elapsed = this.clock.getElapsedTime()
        if(this.clock.running && elapsed < this.clockLimit) {
            const vProgress = sineInOut(elapsed/this.clockLimit);
            //console.log('there was still a transition in progress', elapsed, this.clockLimit, vProgress)
            this.positions = this.tweenStart.pos.map((p, i) => p + (this.tweenEnd.pos[i] - p) * vProgress)
            this.colors = this.tweenStart.clr.map((p, i) => p + (this.tweenEnd.clr[i] - p) * vProgress)
        }

        //now we can safely reassign the targets
        this.targetColors = colors;
        this.targetPositions = positions;
            
        const currentPair = {
            pos: this.positions,
            clr: this.colors
        }
        const targetPair = {
            pos: this.targetPositions,
            clr: this.targetColors
        }

        let start = currentPair;
        let end = targetPair;
        const diff = targetPair.pos.length - currentPair.pos.length

        //make the start and end array of equal size
        if(diff > 0) {
            //console.log('target is larger')
            start = adjustArrayTo(targetPair, currentPair);
        } else if(diff < 0) {
            //console.log('target is smaller')
            end = adjustArrayTo(currentPair, targetPair);
        }

        this.tweenStart = start
        this.tweenEnd = end

        //tween attributes
        this.mesh.geometry.setAttribute('position', new THREE.Float32BufferAttribute( start.pos, 3 ) );
        this.mesh.geometry.setAttribute('targetPosition', new THREE.Float32BufferAttribute( end.pos, 3 ) );
        this.mesh.geometry.setAttribute('color', new THREE.Float32BufferAttribute( start.clr, 3 ) );
        this.mesh.geometry.setAttribute('targetColor', new THREE.Float32BufferAttribute( end.clr, 3 ) );

        //point size tween
        this.sizeTween && this.sizeTween.kill();
        this.sizeTween = TweenLite.to(this.mesh.material.uniforms.size, this.clockLimit, {
            value: psize,
            ease: Sine.easeInOut
        });

        if(this.transitioningToNewPage) {
            this.transitioningToNewPage = false;
            
            //we came from another page. 
            //just set duration here again for consistency
            //(was already set by handleLink for distortion)
            this.setTransitionDuration(Particles.clockLimitNextPage);
            window.setTimeout(() => {
                //enable scrolling once the transition settled
                gamEvent('gamLinkComplete');
            }, Particles.clockLimitNextPage*1000)
        } else {
            this.setTransitionDuration(Particles.clockLimitSamePage);

        }
        
        this.clock.start();
    }


    handleLink = () => {

        this.currentHelper && this.currentHelper.pause();

        //distort current positions
        this.targetPositions = this.positions.map(p => p + (Math.random() - 0.5) * 2)
        this.mesh.geometry.setAttribute('targetPosition', new THREE.Float32BufferAttribute( this.targetPositions, 3));
        
        this.tweenStart.pos = this.positions
        this.tweenEnd.pos = this.targetPositions
        this.tweenStart.clr = this.tweenEnd.clr = this.colors

        this.transitioningToNewPage = true;
        this.setTransitionDuration(Particles.clockLimitNextPage);
        this.clock.start();
    }

    animate = () => {

        //target transitions
        if(this.clock.running) {
            const elapsed = this.clock.getElapsedTime();
            this.mesh.material.uniforms.elapsedTime.value = elapsed;

            if(elapsed >= this.clockLimit) {
                //console.log('transition complete')
                this.clock.stop();
                
                this.colors = this.targetColors;
                this.positions = this.targetPositions;

                this.mesh.geometry.setAttribute('color', new THREE.Float32BufferAttribute( this.colors, 3 ) );
                this.mesh.geometry.setAttribute('position', new THREE.Float32BufferAttribute( this.positions, 3 ) );
                this.mesh.material.uniforms.elapsedTime.value = 0;

                if(!this.transitioningToNewPage) this.currentHelper.play();
            }
        };

        //update via video helper
        if(this.currentHelper && this.currentHelper.video && this.currentHelper.isPlaying) {
            const { positions, colors } = this.currentHelper.readCanvas();
            
            this.colors = this.targetColors = colors;
            this.positions = this.targetPositions = positions;
            const p32 = new THREE.Float32BufferAttribute( positions, 3 )
            const c32 = new THREE.Float32BufferAttribute( colors, 3 )

            this.mesh.geometry.setAttribute('position', p32);
            this.mesh.geometry.setAttribute('targetPosition', p32);
            this.mesh.geometry.setAttribute('color', c32);
            this.mesh.geometry.setAttribute('targetColor', c32);
            this.mesh.material.uniforms.elapsedTime.value = 0;
        }
    }

    setTransitionDuration = (duration) => {
        this.clockLimit = duration;
        this.mesh.material.uniforms.duration.value = duration;
    }
}



function adjustArrayTo(master, slave) {

    const every = master.pos.length / slave.pos.length;
    const everyR = Math.round(every);
    //console.log('master is larger by', every)

    const newSlavePos = []
    const newSlaveClr = []

    //for the many "master" points, create an equal amount of (overlapping) "slave" points
    for(let i=0; i<master.pos.length/3; i++) {
        const k = Math.floor(i/every)*3;
        newSlavePos.push(
            slave.pos[k],
            slave.pos[k+1],
            slave.pos[k+2],
        )

        //prevent too bright colors from additive blending many slave points
        //make them black instead (hidden)
        if(i % everyR === 0) {
            newSlaveClr.push(
                slave.clr[k],
                slave.clr[k+1],
                slave.clr[k+2],
            )
        } else {
            newSlaveClr.push(0,0,0);
        }
    }

    return { pos: newSlavePos, clr: newSlaveClr }
}